import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TextField,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  CheckCircle,
  Print,
  Close,
  Save,
  Cancel,
  Receipt,
} from "@mui/icons-material";
import { purchaseAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';
// Print-specific styles (same as before)
const printStyles = `
  @media print {
    @page {
      margin: 0.5in;
      size: letter;
    }
    
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .no-print {
      display: none !important;
    }
    
    .print-only {
      display: block !important;
    }
    
    .print-break-before {
      page-break-before: always;
    }
    
    .print-break-after {
      page-break-after: always;
    }
    
    .print-break-inside-avoid {
      page-break-inside: avoid;
    }
    
    .purchase-order-print {
      font-family: 'Arial', sans-serif;
      color: #000;
      line-height: 1.4;
    }
    
    .print-header {
      border-bottom: 2px solid #333;
      padding-bottom: 15px;
      margin-bottom: 20px;
    }
    
    .print-logo {
      max-width: 200px;
      height: auto;
    }
    
    .print-section {
      margin-bottom: 25px;
      page-break-inside: avoid;
    }
    
    .print-table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0;
    }
    
    .print-table th {
      background-color: #f5f5f5 !important;
      border: 1px solid #ddd;
      padding: 8px 12px;
      text-align: left;
      font-weight: bold;
      -webkit-print-color-adjust: exact;
    }
    
    .print-table td {
      border: 1px solid #ddd;
      padding: 8px 12px;
    }
    
    .print-totals {
      background-color: #f9f9f9 !important;
      border: 1px solid #ddd;
      padding: 15px;
      margin-top: 20px;
      -webkit-print-color-adjust: exact;
    }
    
    .print-watermark {
      position: fixed;
      bottom: 20px;
      right: 20px;
      opacity: 0.1;
      font-size: 48px;
      transform: rotate(-45deg);
      pointer-events: none;
    }
    
    .print-footer {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      text-align: center;
      font-size: 12px;
      color: #666;
      border-top: 1px solid #ddd;
      padding: 10px;
      background: white;
    }
    
    /* Hide Material-UI specific elements */
    .MuiCard-root, .MuiPaper-root {
      box-shadow: none !important;
      border: 1px solid #ddd !important;
    }
    
    .MuiButton-root {
      display: none !important;
    }
    
    .MuiStepper-root {
      padding: 0 !important;
    }
  }
  
  @media screen {
    .print-only {
      display: none !important;
    }
  }
`;

export const InvPurchaseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editable fields state
  const [editableData, setEditableData] = useState({
    deliveryInfo: "",
    termsAndConditions: "",
    shippingMethod: "",
    deliveryAddress: "",
    notes: "",
  });

  const printContentRef = useRef(null);

  useEffect(() => {
    fetchPurchaseOrder();
  }, [id]);

  const fetchPurchaseOrder = async () => {
    try {
      const response = await purchaseAPI.getById(id);
      const order = response.data.data.purchaseOrder;
      setPurchaseOrder(order);

      // Initialize editable data
      setEditableData({
        deliveryInfo: order.deliveryInfo || "",
        termsAndConditions:
          order.termsAndConditions ||
          `1. Goods must be in original packaging\n2. All items must have minimum 6 months validity\n3. Delivery must be accompanied with invoice\n4. Damaged goods will not be accepted`,
        shippingMethod: order.shippingMethod || "",
        deliveryAddress: order.deliveryAddress || "",
        notes: order.notes || "",
      });
    } catch (error) {
      enqueueSnackbar("Error fetching purchase order details", {
        variant: "error",
      });
      navigate("/hospital/hospital-inventory/purchases");
    } finally {
      setLoading(false);
    }
  };

  const getStatusSteps = () => {
    return ["Draft", "Sent", "Confirmed", "Partially Received", "Completed"];
  };

  const getActiveStep = (status) => {
    const statusMap = {
      draft: 0,
      sent: 1,
      confirmed: 2,
      partially_received: 3,
      completed: 4,
    };
    return statusMap[status] || 0;
  };

  const handlePrint = () => {
    setPrintDialogOpen(true);
  };

  const handleDirectPrint = () => {
    const printWindow = window.open("", "_blank");
    const printContent = generatePrintContent();

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Purchase Order - ${purchaseOrder.poNumber}</title>
          <style>${printStyles}</style>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              color: #000;
            }
            .print-container { 
              max-width: 100%; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              setTimeout(() => window.close(), 1000);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset to original data
      setEditableData({
        deliveryInfo: purchaseOrder.deliveryInfo || "",
        termsAndConditions:
          purchaseOrder.termsAndConditions ||
          `1. Goods must be in original packaging\n2. All items must have minimum 6 months validity\n3. Delivery must be accompanied with invoice\n4. Damaged goods will not be accepted`,
        shippingMethod: purchaseOrder.shippingMethod || "",
        deliveryAddress: purchaseOrder.deliveryAddress || "",
        notes: purchaseOrder.notes || "",
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!purchaseOrder) return;

    setSaving(true);
    try {
      const updateData = {
        deliveryInfo: editableData.deliveryInfo,
        termsAndConditions: editableData.termsAndConditions,
        shippingMethod: editableData.shippingMethod,
        deliveryAddress: editableData.deliveryAddress,
        notes: editableData.notes,
      };

      await purchaseAPI.update(id, updateData);

      // Update local state
      setPurchaseOrder((prev) => ({
        ...prev,
        ...updateData,
      }));

      enqueueSnackbar("Purchase order updated successfully", {
        variant: "success",
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Update error:", error);
      const errorMessage =
        error.response?.data?.message || "Error updating purchase order";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleEditableChange = (field, value) => {
    setEditableData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const generatePrintContent = () => {
    if (!purchaseOrder) return "";

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(amount || 0);
    };

    // Use editable data for print content
    const currentData = isEditing
      ? editableData
      : {
          deliveryInfo: purchaseOrder.deliveryInfo || "",
          termsAndConditions:
            purchaseOrder.termsAndConditions ||
            `1. Goods must be in original packaging\n2. All items must have minimum 6 months validity\n3. Delivery must be accompanied with invoice\n4. Damaged goods will not be accepted`,
          shippingMethod: purchaseOrder.shippingMethod || "",
          deliveryAddress: purchaseOrder.deliveryAddress || "",
          notes: purchaseOrder.notes || "",
        };

    return `
      <div class="purchase-order-print">
        <!-- Header -->
        <div class="print-header" style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px;">
          <div>
            <h1 style="margin: 0; color: #1976d2; font-size: 28px;">PURCHASE ORDER</h1>
            <p style="margin: 5px 0; font-size: 14px; color: #666;">PO Number: ${
              purchaseOrder.poNumber
            }</p>
            <p style="margin: 5px 0; font-size: 14px; color: #666;">Date: ${formatDate(
              purchaseOrder.orderDate
            )}</p>
          </div>
          <div style="text-align: right;">
            <div style="padding: 10px; border: 2px solid #1976d2; display: inline-block;">
              <strong style="color: #1976d2; font-size: 16px;">${purchaseOrder.status
                .replace("_", " ")
                .toUpperCase()}</strong>
            </div>
          </div>
        </div>

        <!-- Company and Supplier Info -->
        <div style="display: flex; justify-content: space-between; margin-bottom: 30px;" class="print-break-inside-avoid">
          <div style="flex: 1;">
            <h3 style="margin: 0 0 10px 0; color: #333;">FROM</h3>
            <div style="background: #f5f5f5; padding: 15px; border-radius: 4px;">
              <strong style="font-size: 16px;">Your Pharmacy Name</strong><br/>
              123 Medical Street<br/>
              Healthcare City, HC 12345<br/>
              Phone: (555) 123-4567<br/>
              Email: pharmacy@example.com<br/>
              GSTIN: 07AABCU9603R1ZM
            </div>
          </div>
          
          <div style="flex: 1; margin-left: 30px;">
            <h3 style="margin: 0 0 10px 0; color: #333;">TO</h3>
            <div style="background: #f5f5f5; padding: 15px; border-radius: 4px;">
              <strong style="font-size: 16px;">${
                purchaseOrder.supplier.name
              }</strong><br/>
              ${purchaseOrder.supplier.address?.street || "N/A"}<br/>
              ${purchaseOrder.supplier.address?.city || ""}, ${
      purchaseOrder.supplier.address?.state || ""
    } - ${purchaseOrder.supplier.address?.zipCode || ""}<br/>
              Phone: ${purchaseOrder.supplier.phone}<br/>
              Email: ${purchaseOrder.supplier.email}<br/>
              Contact: ${purchaseOrder.supplier.contactPerson}
            </div>
          </div>
        </div>

        <!-- Order Items -->
        <div class="print-section print-break-inside-avoid">
          <h3 style="margin: 0 0 15px 0; color: #333; border-bottom: 2px solid #333; padding-bottom: 5px;">ORDER ITEMS</h3>
          <table class="print-table">
            <thead>
              <tr>
                <th style="width: 5%;">#</th>
                <th style="width: 35%;">Item Description</th>
                <th style="width: 10%; text-align: center;">Quantity</th>
                <th style="width: 15%; text-align: right;">Unit Price</th>
                <th style="width: 15%; text-align: right;">Tax Rate</th>
                <th style="width: 20%; text-align: right;">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              ${purchaseOrder.items
                .map(
                  (item, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>
                    <strong>${item.medicine?.name || "N/A"}</strong><br/>
                    <small>${item.medicine?.genericName || ""} • ${
                    item.medicine?.strength || ""
                  } • ${item.medicine?.form || ""}</small><br/>
                    <small>MFG: ${item.medicine?.manufacturer || "N/A"}</small>
                  </td>
                  <td style="text-align: center;">${item.quantity}</td>
                  <td style="text-align: right;">${formatCurrency(
                    item.costPrice
                  )}</td>
                  <td style="text-align: right;">${item.taxRate || 0}%</td>
                  <td style="text-align: right;">${formatCurrency(
                    item.quantity * item.costPrice
                  )}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </div>

        <!-- Totals -->
        <div class="print-totals print-break-inside-avoid">
          <div style="display: flex; justify-content: space-between; max-width: 300px; margin-left: auto;">
            <div style="text-align: right;">
              <div style="margin-bottom: 8px;"><strong>Subtotal:</strong></div>
              <div style="margin-bottom: 8px;"><strong>Tax Amount:</strong></div>
              <div style="margin-bottom: 8px; border-top: 1px solid #ddd; padding-top: 8px;"><strong>Grand Total:</strong></div>
            </div>
            <div style="text-align: right;">
              <div style="margin-bottom: 8px;">${formatCurrency(
                purchaseOrder.totalAmount
              )}</div>
              <div style="margin-bottom: 8px;">${formatCurrency(
                purchaseOrder.taxAmount
              )}</div>
              <div style="margin-bottom: 8px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 18px;">
                ${formatCurrency(purchaseOrder.grandTotal)}
              </div>
            </div>
          </div>
        </div>

        <!-- Additional Information -->
        <div style="display: flex; justify-content: space-between; margin-top: 30px;" class="print-break-before">
          <div style="flex: 1;">
            <h3 style="margin: 0 0 10px 0; color: #333;">DELIVERY INFORMATION</h3>
            <div style="background: #f5f5f5; padding: 15px; border-radius: 4px; white-space: pre-line;">
              ${
                currentData.deliveryInfo ||
                `Expected Delivery: ${
                  purchaseOrder.expectedDeliveryDate
                    ? formatDate(purchaseOrder.expectedDeliveryDate)
                    : "Not specified"
                }\n\nDelivery Address:\nYour Pharmacy Name\n123 Medical Street, Healthcare City, HC 12345`
              }
            </div>
          </div>
          
          <div style="flex: 1; margin-left: 30px;">
            <h3 style="margin: 0 0 10px 0; color: #333;">TERMS & CONDITIONS</h3>
            <div style="background: #f5f5f5; padding: 15px; border-radius: 4px; font-size: 12px; white-space: pre-line;">
              ${
                currentData.termsAndConditions ||
                `1. Goods must be in original packaging\n2. All items must have minimum 6 months validity\n3. Delivery must be accompanied with invoice\n4. Damaged goods will not be accepted`
              }
              ${
                currentData.notes
                  ? `\n\nSpecial Notes:\n${currentData.notes}`
                  : ""
              }
            </div>
          </div>
        </div>

        <!-- Signatures -->
        <div style="display: flex; justify-content: space-between; margin-top: 60px;" class="print-break-inside-avoid">
          <div style="text-align: center;">
            <div style="border-top: 1px solid #000; width: 200px; padding-top: 10px;">
              <strong>Authorized Signature</strong><br/>
              (Supplier)
            </div>
          </div>
          <div style="text-align: center;">
            <div style="border-top: 1px solid #000; width: 200px; padding-top: 10px;">
              <strong>Authorized Signature</strong><br/>
              (Pharmacy)
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="print-footer">
          <p>Purchase Order: ${
            purchaseOrder.poNumber
          } | Generated on ${new Date().toLocaleDateString()} | Page 1 of 1</p>
        </div>

        <!-- Watermark -->
        <div class="print-watermark">
          ${
            purchaseOrder.status === "completed"
              ? "RECEIVED"
              : purchaseOrder.status === "cancelled"
              ? "CANCELLED"
              : "CONFIDENTIAL"
          }
        </div>
      </div>
    `;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!purchaseOrder) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" color="error">
          Purchase order not found
        </Typography>
        <Button onClick={() => navigate("/hospital/hospital-inventory/purchases")} sx={{ mt: 2 }}>
          Back to Purchase Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <style>{printStyles}</style>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
        className="no-print"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/hospital/hospital-inventory/purchases")}
          >
            Back
          </Button>
          <Typography variant="h4" fontWeight="bold">
            Purchase Order: {purchaseOrder.poNumber}
          </Typography>
          <Chip
            label={purchaseOrder.status.replace("_", " ")}
            color={
              purchaseOrder.status === "completed"
                ? "success"
                : purchaseOrder.status === "cancelled"
                ? "error"
                : purchaseOrder.status === "partially_received"
                ? "warning"
                : "primary"
            }
          />

          {(purchaseOrder.status === "completed" ||
            purchaseOrder.status === "received" ||
            purchaseOrder.status === "partially_received") && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Receipt />}
              onClick={() => navigate(`/hospital/hospital-inventory/purchases/${id}/grn`)}
              sx={{ ml: 2 }}
            >
              View GRN
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Print Purchase Order">
            <Button
              startIcon={<Print />}
              onClick={handlePrint}
              variant="outlined"
            >
              Print
            </Button>
          </Tooltip>

          {hasAnyRole(["admin", "inventory_manager"]) && (
            <>
              {isEditing ? (
                <>
                  <Button
                    startIcon={<Cancel />}
                    onClick={handleEditToggle}
                    variant="outlined"
                    color="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    startIcon={<Save />}
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : (
                <Button
                  startIcon={<Edit />}
                  onClick={handleEditToggle}
                  variant="contained"
                >
                  Edit Details
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {isEditing && (
        <Alert severity="info" sx={{ mb: 2 }} className="no-print">
          You are editing delivery information and terms & conditions. These
          changes will be reflected in printed documents.
        </Alert>
      )}

      {/* Print Preview Dialog */}
      <Dialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{ "& .MuiDialog-paper": { height: "90vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            Print Preview - {purchaseOrder.poNumber}
          </Typography>
          <IconButton onClick={() => setPrintDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            ref={printContentRef}
            sx={{
              height: "100%",
              overflow: "auto",
              "& .print-container": {
                p: 2,
                bgcolor: "background.paper",
              },
            }}
            dangerouslySetInnerHTML={{ __html: generatePrintContent() }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Typography variant="body2" color="text.secondary">
            This is a preview. Actual print may vary slightly.
          </Typography>
          <Box>
            <Button onClick={() => setPrintDialogOpen(false)} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleDirectPrint}
              variant="contained"
              startIcon={<Print />}
            >
              Print Now
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Main Content - Hidden during print */}
      <div className="no-print">
        <Grid container spacing={3}>
          {/* Main Details */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Order Items
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseOrder.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="subtitle2">
                              {item.item?.name || "N/A"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.item?.brand || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {item.item?.manufacturer || ""}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.item?.itemType} • {item.item?.unit}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">₹{item.costPrice}</TableCell>
                          <TableCell align="right">
                            ₹{(item.quantity * item.costPrice).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 3, textAlign: "right" }}>
                  <Typography variant="body1" gutterBottom>
                    Subtotal: ₹{purchaseOrder.totalAmount?.toFixed(2)}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Tax: ₹{purchaseOrder.taxAmount?.toFixed(2)}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    Grand Total: ₹{purchaseOrder.grandTotal?.toFixed(2)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Shipping & Delivery Information - Now Editable */}
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Delivery Information
                  </Typography>
                  {isEditing && (
                    <Chip label="Editing" color="primary" size="small" />
                  )}
                </Box>

                {isEditing ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Shipping Method"
                        value={editableData.shippingMethod}
                        onChange={(e) =>
                          handleEditableChange("shippingMethod", e.target.value)
                        }
                        placeholder="e.g., Express, Standard, etc."
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Expected Delivery Date"
                        value={
                          purchaseOrder.expectedDeliveryDate
                            ? purchaseOrder.expectedDeliveryDate.split("T")[0]
                            : ""
                        }
                        InputLabelProps={{ shrink: true }}
                        disabled
                        helperText="Edit in main purchase order"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Delivery Address"
                        value={editableData.deliveryAddress}
                        onChange={(e) =>
                          handleEditableChange(
                            "deliveryAddress",
                            e.target.value
                          )
                        }
                        placeholder="Enter complete delivery address"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Delivery Instructions & Information"
                        value={editableData.deliveryInfo}
                        onChange={(e) =>
                          handleEditableChange("deliveryInfo", e.target.value)
                        }
                        placeholder="Enter any special delivery instructions, contact information, or additional delivery details..."
                        helperText="This will appear in the printed purchase order"
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Shipping Method
                      </Typography>
                      <Typography variant="body1">
                        {purchaseOrder.shippingMethod || "Not specified"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Expected Delivery Date
                      </Typography>
                      <Typography variant="body1">
                        {purchaseOrder.expectedDeliveryDate
                          ? new Date(
                              purchaseOrder.expectedDeliveryDate
                            ).toLocaleDateString()
                          : "Not specified"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Delivery Address
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-line" }}
                      >
                        {purchaseOrder.deliveryAddress || "Not specified"}
                      </Typography>
                    </Grid>
                    {purchaseOrder.deliveryInfo && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Additional Delivery Information
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {purchaseOrder.deliveryInfo}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Order Timeline */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Order Status
                </Typography>
                <Stepper
                  activeStep={getActiveStep(purchaseOrder.status)}
                  orientation="vertical"
                >
                  {getStatusSteps().map((step, index) => (
                    <Step key={step}>
                      <StepLabel StepIconComponent={CheckCircle}>
                        {step}
                      </StepLabel>
                      <StepContent>
                        <Typography variant="caption">
                          {index === getActiveStep(purchaseOrder.status)
                            ? "Current status"
                            : ""}
                        </Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </CardContent>
            </Card>

            {/* Terms & Conditions - Now Editable */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Terms & Conditions
                  </Typography>
                  {isEditing && (
                    <Chip label="Editing" color="primary" size="small" />
                  )}
                </Box>

                {isEditing ? (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      label="Terms & Conditions"
                      value={editableData.termsAndConditions}
                      onChange={(e) =>
                        handleEditableChange(
                          "termsAndConditions",
                          e.target.value
                        )
                      }
                      placeholder="Enter terms and conditions (one per line)"
                      helperText="Each line will be treated as a separate term"
                    />
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Additional Notes"
                      value={editableData.notes}
                      onChange={(e) =>
                        handleEditableChange("notes", e.target.value)
                      }
                      placeholder="Any additional notes or special instructions..."
                      sx={{ mt: 2 }}
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-line", mb: 2 }}
                    >
                      {purchaseOrder.termsAndConditions ||
                        `1. Goods must be in original packaging\n2. All items must have minimum 6 months validity\n3. Delivery must be accompanied with invoice\n4. Damaged goods will not be accepted`}
                    </Typography>
                    {purchaseOrder.notes && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" color="text.secondary">
                          Additional Notes:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {purchaseOrder.notes}
                        </Typography>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Supplier Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Supplier Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Supplier Name
                  </Typography>
                  <Typography variant="body1">
                    {purchaseOrder.supplier.name}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Contact Person
                  </Typography>
                  <Typography variant="body1">
                    {purchaseOrder.supplier.contactPerson}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {purchaseOrder.supplier.email}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">
                    {purchaseOrder.supplier.phone}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Order Information */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Order Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Order Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(purchaseOrder.orderDate).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body1">
                    {purchaseOrder.createdBy?.name || "System"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {new Date(purchaseOrder.updatedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
    </Box>
  );
};
